import { type SoapContentItem } from '@/shared/types/yakurekiSoap'

// トレーシングレポート提出ルール (店舗×医師の約束事)。
// 「対象の医薬品グループが新規処方されたら提出する」以外の情報を持たない (back tracing_report)。
export interface TracingReportRule {
  id: string
  drug_group: string
  drug_group_name: string
  created_at: string
}

// 検知時に固定化されたスナップショット。ルールの変更/削除後も表示が自己完結する。
export interface TracingReportSnapshot {
  rule?: {
    drug_group_name: string
  } | null
  matched?: {
    yj_code: string
    name: string
  }[]
  prescriber?: {
    institution_name?: string
    department_name?: string
    doctor_name?: string
  }
}

// トレーシングレポート文書 (検知〜生成の1文書)。
// rule 非 null = ルール検知起点 (医師指示) / rule null = 手動起点 (薬剤師判断)。
// report_json が null の間は「検知のみ (未生成)」。
export interface TracingReportItem {
  id: string
  rule: string | null
  snapshot: TracingReportSnapshot
  report_json: SoapContentItem[] | null
  created_at: string
}

// 一覧・meta 行の自然文表記 (ルール表示と検知理由表示で共用)
export const describeTracingRule = (rule: TracingReportRule): string =>
  `「${rule.drug_group_name}」が新規処方されたら、トレーシングレポートを提出しその旨をSOAPに記載`

// 検知理由 meta は label: value の 2 スロット 2 行 (design-book 04-apps/kakeru.md)。
//   ルール: {グループ名} / {薬剤名}の新規処方
//   宛先: {医療機関名} {医師名}
// prescriber 不明なら 2 行目省略。手動起点は `手動で作成` 1 行。
export const describeItemOriginLines = (item: TracingReportItem): string[] => {
  const snapshot = item.snapshot ?? {}
  if (!snapshot.rule) return ['手動で作成']
  const names = (snapshot.matched ?? [])
    .map((m) => m.name)
    .filter(Boolean)
    .join('、')
  const ruleLine = names
    ? `ルール: ${snapshot.rule.drug_group_name} / ${names}の新規処方`
    : `ルール: ${snapshot.rule.drug_group_name}`
  const prescriber = snapshot.prescriber ?? {}
  const target = [prescriber.institution_name, prescriber.doctor_name]
    .filter(Boolean)
    .join(' ')
  return target ? [ruleLine, `宛先: ${target}`] : [ruleLine]
}
